/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  EnvironmentType,
  KpiTimePeriod,
  useEnvironmentKpiQuery,
} from "~/generated/graphql";
import { KpiType } from "~/constants";
import { convertToPercent, isNotNil, Maybe } from "~/tools";

type Props = {
  bcId: string;
  envId: string;
  timePeriod: KpiTimePeriod;
  kpiType: KpiType | undefined;
  environmentType: EnvironmentType | undefined | null;
};

export function useEnvironmentKpis({
  bcId,
  envId,
  timePeriod,
  kpiType,
  environmentType,
}: Props) {
  const {
    data,
    previousData,
    loading: kpiLoading,
    error: kpiError,
  } = useEnvironmentKpiQuery({
    variables: {
      bcId,
      envId,
      timePeriod,
      ...getKpiFetchConfig(kpiType, environmentType),
    },
  });

  const { nonProdHours, overallWaste } =
    data?.environment?.summaryKpis ??
    previousData?.environment?.summaryKpis ??
    {};
  const {
    nonProdHours: nonProdHoursDetails,
    overallWaste: overallWasteDetails,
  } = data?.environment?.kpis ?? {};

  return {
    summary: mapSummary({ nonProdHours, overallWaste }),
    nonProdHours: mapKpiDetails(nonProdHoursDetails?.details),
    overallWaste: mapKpiDetails(overallWasteDetails?.details),
    kpiLoading,
    kpiError,
  };
}

function getKpiFetchConfig(
  selectedType: KpiType | undefined,
  environmentType: Maybe<EnvironmentType>
) {
  return {
    fetchOverallWaste: selectedType === KpiType.OVERALL_WASTE,
    fetchNonProdHours:
      selectedType === KpiType.NON_PROD_HOURS &&
      environmentType !== EnvironmentType.Production,
  };
}

type Summary = Partial<
  NonNullable<
    NonNullable<
      NonNullable<
        ReturnType<typeof useEnvironmentKpiQuery>["data"]
      >["environment"]
    >["summaryKpis"]
  >
>;

function mapSummary({ nonProdHours, overallWaste }: Summary) {
  return {
    nonProdHours: {
      value: nonProdHours?.details?.metrics?.currentValue,
      target: nonProdHours?.details?.targets?.currentTarget?.goal,
    },
    overallWaste: {
      value: overallWaste?.details?.metrics?.currentValue,
      target: overallWaste?.details?.targets?.currentTarget?.goal,
    },
  };
}

type KpiDetails = Partial<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<
          ReturnType<typeof useEnvironmentKpiQuery>["data"]
        >["environment"]
      >["kpis"]
    >["nonProdHours" | "overallWaste"]
  >["details"]
>;

function mapKpiDetails(kpiDetails: KpiDetails | undefined) {
  if (!kpiDetails) {
    return;
  }
  const currentValueRaw = kpiDetails?.metrics?.currentValue;

  return {
    currentValue: currentValueRaw
      ? convertToPercent(currentValueRaw)
      : undefined,
    targetValue: kpiDetails?.targets?.currentTarget?.goal,
    timePoints: kpiDetails?.metrics?.values ?? [],
    targets:
      kpiDetails?.targets?.targets?.filter(isNotNil).map(mapTargets) ?? [],
    currentTarget: kpiDetails.targets?.currentTarget,
  };
}

type KpiTarget = NonNullable<KpiDetails["targets"]>["targets"][number];

function mapTargets({ goal, startDate, endDate, name }: KpiTarget) {
  return {
    name,
    value: goal.toString(),
    startDate,
    endDate,
  };
}
